import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AdminGate } from "../../components/admin/AdminGate";
import { useToast } from "../../components/toast/toastContext";
import { ErrorState, LoadingState } from "../../components/states";
import panel from "../../components/admin/AdminPanel.module.css";
import { NumberField, SettingForm, SettingPanel } from "../../components/admin/SettingPanel";
import styles from "./WriteSafetyPage.module.css";
import { fetchWriteSafetySetting, updateWriteSafetySetting, type WriteSafetySetting } from "../../api/writeSafety";

const SettingsWritePermission = "settings.write";

/**
 * The "Write-safety controls" screen
 * `AGENTS.md`'s blast-radius constraint requires: the kill switch and the change bound (write-enable is per provider, on Sync status). The kill switch is its own panel above everything else on the page, its
 * state said in words at the top and the whole panel lit in the danger colour while it is on, because this is the literal control the
 * security constraint is about, and its current state must be readable at a glance, not discovered by scrolling to a checkbox.
 */
export function WriteSafetyPage(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <AdminGate permissions={[SettingsWritePermission]} title={t("admin.writeSafety.denied.title")} description={t("admin.writeSafety.denied.description")}>
        <WriteSafetyContent />
      </AdminGate>
    </div>
  );
}

/** The kill switch, then the change bound. */
export function WriteSafetyContent(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; setting: WriteSafetySetting } | { kind: "error"; message: string }>({ kind: "loading" });
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchWriteSafetySetting();
    if (result.kind === "ok") {
      setState({ kind: "loaded", setting: result.response });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.writeSafety.denied.description") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = async (changeBound: number, killSwitchEnabled: boolean) => {
    setPending(true);
    const result = await updateWriteSafetySetting(changeBound, killSwitchEnabled);
    setPending(false);
    if (result.kind === "ok") {
      setState({ kind: "loaded", setting: result.response });
      toast.show(t("admin.writeSafety.saved"), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.writeSafety.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("admin.writeSafety.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  const { setting } = state;
  const on = setting.killSwitchEnabled;

  return (
    <>
      <section className={`${panel.panel} ${styles.killSwitch}`} data-on={on || undefined} aria-labelledby="kill-switch-heading">
        <div className={panel.header}>
          <h2 id="kill-switch-heading" className={`panel-title ${panel.title}`}>
            {t("admin.writeSafety.killSwitch.title")}
          </h2>
        </div>
        <p className={styles.killSwitchState} role="status">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            {on ? (
              <path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86L7.86 2zM12 8v4M12 16h.01" />
            ) : (
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-4" />
            )}
          </svg>
          {on ? t("admin.writeSafety.killSwitch.stateOn") : t("admin.writeSafety.killSwitch.stateOff")}
        </p>
        <div className={styles.killSwitchFoot}>
          <p className={styles.killSwitchExplanation}>
            {t("admin.writeSafety.killSwitch.explanation")} {t("admin.writeSafety.killSwitch.audience")} <Link to="/admin/system/features">{t("admin.writeSafety.killSwitch.audienceLink")}</Link>
          </p>
          <button type="button" className={on ? "button button-secondary" : "button button-primary"} disabled={pending} onClick={() => void apply(setting.changeBound, !on)}>
            {on ? t("admin.writeSafety.killSwitch.disable") : t("admin.writeSafety.killSwitch.enable")}
          </button>
        </div>
      </section>

      <ChangeBoundPanel setting={setting} pending={pending} onApply={apply} />
    </>
  );
}

/** How many people one run may change at a vendor, with when it was last changed beneath. */
function ChangeBoundPanel({
  setting,
  pending,
  onApply,
}: {
  setting: WriteSafetySetting;
  pending: boolean;
  onApply: (changeBound: number, killSwitchEnabled: boolean) => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [changeBound, setChangeBound] = useState(String(setting.changeBound));

  return (
    <SettingPanel id="change-bound" title={t("admin.writeSafety.controls.title")} intro={t("admin.writeSafety.controls.explanation")}>
      <SettingForm>
        <NumberField id="write-safety-change-bound" label={t("admin.writeSafety.controls.changeBound")} min={0} value={changeBound} onChange={setChangeBound} />
        <button type="button" className="button button-secondary" disabled={pending} onClick={() => void onApply(Number(changeBound), setting.killSwitchEnabled)}>
          {t("admin.writeSafety.controls.save")}
        </button>
      </SettingForm>
      <p className={panel.footnotes}>
        {t("admin.writeSafety.updatedAt", {
          updatedAt: new Intl.DateTimeFormat(navigator.language, { dateStyle: "long", timeStyle: "short" }).format(new Date(setting.updatedAt)),
        })}
      </p>
    </SettingPanel>
  );
}
